// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import Img, { ImgProps } from "@werner/MobiusOverrides/Img/Img";
import { mergeWithCss, parseBoolean } from "@werner/XDL/Utilities";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductImageStyles;
    showSmallImage?: boolean;
}

type Props = OwnProps;

export interface ProductImageStyles {
    linkWrappingImage?: ClickablePresentationProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
}

export const productImageStyles: ProductImageStyles = {
    linkWrappingImage: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    image: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    img: {
        css: css`
            max-width: 100%;
            max-height: 100%;
            height: auto;
            width: auto;
        `,
    },
};

const ProductImage = ({ product, onClick, extendedStyles, showSmallImage }: Props) => {
    const [styles] = useState(() => mergeWithCss(productImageStyles, extendedStyles));

    let altText = "product" in product ? product.product.imageAltText : product.altText;
    if (!altText || altText === "") {
        altText = "product" in product ? product.product.productTitle : product.productName;
    }
    const { notFoundImage } = useAppSelector(state => {
        const { notFoundSmallImagePath, notFoundMediumImagePath, notFoundLargeImagePath } =
            getSettingsCollection(state).customSettings;
        return {
            notFoundImage: showSmallImage
                ? notFoundSmallImagePath || notFoundMediumImagePath
                : notFoundMediumImagePath || notFoundLargeImagePath,
        };
    });
    const imagePath =
        "product" in product
            ? showSmallImage
                ? product.product.smallImagePath || product.product.mediumImagePath
                : product.product.mediumImagePath || product.product.smallImagePath
            : product.smallImagePath;

    const widthAndHeight =
        "product" in product
            ? showSmallImage
                ? (product.product.smallImagePath && 100) || (product.product.mediumImagePath && 413)
                : (product.product.mediumImagePath && 413) || (product.product.smallImagePath && 100)
            : 100;

    // XNG-Changes: check if product is event link this product to event details  
    const productProperties = "product" in product ? product.product.properties : product.properties;
    const isEventOrderLine = parseBoolean(productProperties.isEventOrderLine);
    const eventUrl = productProperties.eventUrl ?? "";

    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const productPathLink = (isEventOrderLine && eventUrl) ? eventUrl : productDetailPath;
    const loading = product.idx === undefined ? "lazy" : product.idx < 3 ? "eager" : "lazy";

    // <XNG-Changes>
    const hasProductPath =
        (productDetailPath || (isEventOrderLine && eventUrl)) &&
        (!("isActiveProduct" in product) || product.isActiveProduct);
    if (!hasProductPath) {
        return (
            <Img
                width={widthAndHeight}
                height={widthAndHeight}
                {...styles.img}
                loading={loading}
                src={imagePath || notFoundImage}
                altText={altText}
                disableLazyLoading={true}
            />
        );
    }
    // </XNG-Changes>
    return (
        <Clickable
            {...styles.linkWrappingImage}
            // XNG-Changes             
            href={productPathLink}
            onClick={onClick}
            data-test-selector="productImage"
            aria-label="Product Image Link"
        >
            {/* XNG-Changes */}
            <Img
                width={widthAndHeight}
                height={widthAndHeight}
                {...styles.img}
                loading={loading}
                src={imagePath || notFoundImage}
                altText={altText}
                disableLazyLoading={true}
            />
        </Clickable>
    );
};

export default ProductImage;
